import { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../../store';
import { addNotification } from '../../../store/uiSlice';
import apiService from '../../../services/api';
import {
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  CollisionDetection,
  pointerWithin,
  closestCenter,
  rectIntersection,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { MoodOrderItem } from './types';

export function useMoodDragReorder(moodItems: MoodOrderItem[]) {
  const dispatch = useDispatch<AppDispatch>();

  const [pendingOrder, setPendingOrder] = useState<MoodOrderItem[]>([]);
  const [hasUnsavedOrder, setHasUnsavedOrder] = useState(false);
  const [savingOrder, setSavingOrder] = useState(false);
  const [activeDragId, setActiveDragId] = useState<number | null>(null);
  const [activeDragName, setActiveDragName] = useState<string | null>(null);
  const lastOverIdRef = useRef<string | number | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const customCollisionDetection: CollisionDetection = (args) => {
    const gridContainers = args.droppableContainers.filter(c =>
      String(c.id).startsWith('grid-slot-')
    );
    const cardContainers = args.droppableContainers.filter(c =>
      !String(c.id).startsWith('grid-slot-')
    );

    const pwCollisions = pointerWithin(args);
    if (pwCollisions.length > 0) {
      const gridHits = pwCollisions.filter(c => String(c.id).startsWith('grid-slot-'));
      if (gridHits.length > 0) return gridHits;
      return pwCollisions;
    }

    if (gridContainers.length > 0) {
      const gridRI = rectIntersection({ ...args, droppableContainers: gridContainers });
      if (gridRI.length > 0) return gridRI;
    }

    if (cardContainers.length > 0) {
      return closestCenter({ ...args, droppableContainers: cardContainers });
    }
    return closestCenter(args);
  };

  // Initialize pending order when data loads
  useEffect(() => {
    if (moodItems.length > 0) {
      setPendingOrder(moodItems.map(i => ({ id: i.id, name: i.name, isActive: i.isActive })));
    }
  }, [moodItems]);

  const getOrderedItems = (): MoodOrderItem[] => {
    return pendingOrder.length > 0 ? pendingOrder : moodItems;
  };

  const handleDragStart = (event: DragStartEvent) => {
    lastOverIdRef.current = null;
    const activeId = String(event.active.id);
    const items = getOrderedItems();

    if (activeId.startsWith('grid-drag-')) {
      const slotIndex = parseInt(activeId.replace('grid-drag-', ''), 10);
      const activeItems = items.filter(i => i.isActive);
      const item = activeItems[slotIndex];
      setActiveDragId(item?.id ?? null);
      setActiveDragName(item?.name || null);
      return;
    }

    const draggedId = event.active.id as number;
    setActiveDragId(draggedId);
    const item = items.find(i => i.id === draggedId);
    setActiveDragName(item?.name || null);
  };

  const handleDragOver = (event: DragOverEvent) => {
    if (event.over) lastOverIdRef.current = event.over.id;
  };

  const handleDragCancel = () => {
    setActiveDragId(null);
    setActiveDragName(null);
    lastOverIdRef.current = null;
  };

  const autoSave = useCallback((items: MoodOrderItem[]) => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(async () => {
      try {
        const payload = items.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
        await apiService.reorderAIMoods(payload);
        setHasUnsavedOrder(false);
      } catch {
        setHasUnsavedOrder(true);
      }
    }, 500);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, []);

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveDragId(null);
    setActiveDragName(null);

    const { active, over, collisions } = event;

    let effectiveOverId: string | number | null = over?.id ?? null;
    if (!effectiveOverId && collisions && collisions.length > 0) {
      effectiveOverId = collisions[0].id;
    }
    if (!effectiveOverId) {
      effectiveOverId = lastOverIdRef.current;
    }

    const lastWasGrid = lastOverIdRef.current != null
      && String(lastOverIdRef.current).startsWith('grid-slot-');
    const currentIsGrid = effectiveOverId != null
      && String(effectiveOverId).startsWith('grid-slot-');
    if (lastWasGrid && !currentIsGrid) {
      effectiveOverId = lastOverIdRef.current;
    }

    lastOverIdRef.current = null;
    if (effectiveOverId == null) return;

    const items = getOrderedItems();
    const activeItems = items.filter(i => i.isActive);

    // Resolve source index
    const activeIdStr = String(active.id);
    let sourceIndex: number;
    if (activeIdStr.startsWith('grid-drag-')) {
      const slotIndex = parseInt(activeIdStr.replace('grid-drag-', ''), 10);
      const sourceItem = activeItems[slotIndex];
      if (!sourceItem) return;
      sourceIndex = items.findIndex(i => i.id === sourceItem.id);
    } else {
      sourceIndex = items.findIndex(i => i.id === Number(active.id));
    }

    // Resolve target index
    const overIdStr = String(effectiveOverId);
    let targetIndex: number;
    if (overIdStr.startsWith('grid-slot-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-slot-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) {
        targetIndex = Math.min(slotIndex, items.length - 1);
      } else {
        targetIndex = items.findIndex(i => i.id === targetItem.id);
      }
    } else if (overIdStr.startsWith('grid-drag-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-drag-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) return;
      targetIndex = items.findIndex(i => i.id === targetItem.id);
    } else if (overIdStr.startsWith('grid-')) {
      const slotIndex = parseInt(overIdStr.replace('grid-', ''), 10);
      const targetItem = activeItems[slotIndex];
      if (!targetItem) return;
      targetIndex = items.findIndex(i => i.id === targetItem.id);
    } else {
      targetIndex = items.findIndex(i => i.id === Number(effectiveOverId));
    }

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) return;

    const reordered = arrayMove(items, sourceIndex, targetIndex);
    setPendingOrder(reordered);
    setHasUnsavedOrder(true);
    autoSave(reordered);
  };

  const handleSaveOrder = async () => {
    if (pendingOrder.length === 0) return;
    setSavingOrder(true);
    try {
      const payload = pendingOrder.map((item, index) => ({ id: item.id, sort_order: index + 1 }));
      await apiService.reorderAIMoods(payload);
      dispatch(addNotification({ type: 'success', message: 'Mood order saved' }));
      setHasUnsavedOrder(false);
    } catch (error: any) {
      dispatch(addNotification({ type: 'error', message: error?.message || 'Failed to save order' }));
    } finally {
      setSavingOrder(false);
    }
  };

  const handleDiscardOrder = () => {
    setPendingOrder(moodItems.map(i => ({ id: i.id, name: i.name, isActive: i.isActive })));
    setHasUnsavedOrder(false);
  };

  return {
    pendingOrder,
    hasUnsavedOrder,
    savingOrder,
    activeDragId,
    activeDragName,
    sensors,
    customCollisionDetection,
    getOrderedItems,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    handleDragEnd,
    handleSaveOrder,
    handleDiscardOrder,
  };
}
